import React, { useState } from "react";
import cx from "classnames";
import { constructCurrency } from "common/currency";
import { Clamp } from "@picklebet/ui";
import { isRacingContest } from "@/features/pickems/util/helpers";
import { usePickemsContestWithMarkets } from "@/hooks/firestore/pickems/usePickemsContest";
import { usePermissions } from "@/features/auth/hooks/usePermission";
import { uniqBy } from "lodash";
import { ClampAlert } from "@/features/betslip/components/parts/ClampAlert";
import { getRoute, routeParam } from "@/utilities/route";
import { AddRacePicksToBetslip } from "./AddRacePicksToBetslip";
import { AddSportsPicksToBetslip } from "./AddSportsPicksToBetslip";
import { usePickslipStore } from "@/features/pickems/stores/pickslipStore";
import { PlusIcon } from "@picklebet/ui";
import { AddBetIcon } from "@picklebet/ui";
import { useBetslipStore } from "@/features/betslip/stores/betslipStore";

import * as styles from "./BetNowCta.module.scss";

type BetNowCtaProps = {
  entryId: string;
};

const BetNowCta = ({ entryId }: BetNowCtaProps) => {
  const [submitPicksAsBet, setSubmitPicksAsBet] = useState(false);

  const contestId = routeParam(getRoute(), "contest");

  const { contest, isLoading } = usePickemsContestWithMarkets(contestId);
  const userPermissions = usePermissions();

  const picks = usePickslipStore((state) => state.picks);
  const uniqueContestMarkets = uniqBy(
    contest?.markets,
    ({ event }) => event.id,
  );

  if (isLoading || !contest) return;
  if (
    userPermissions["submitBet"] !== "GRANTED" &&
    userPermissions["viewBettingMarkets"] !== "GRANTED"
  )
    return;

  const isRacing = isRacingContest(contest);

  if (
    userPermissions[isRacing ? "viewRacingMarkets" : "viewBettingMarkets"] !==
    "GRANTED"
  )
    return;

  const AddPicksToBetslip = isRacing
    ? AddRacePicksToBetslip
    : AddSportsPicksToBetslip;

  const pointsAsOdds = picks.reduce((odds, pick) => {
    if (!pick.odds) return odds;

    if (odds === 0) {
      odds = pick.odds;
    } else if (pick.odds > 0) {
      odds = (odds * pick.odds) / 100;
    }

    return odds;
  }, 0);

  return (
    <ClampAlert
      color={`success`}
      icon={<AddBetIcon />}
      clamp={
        <Clamp
          className={cx(styles.flexbox, styles.cta)}
          onClick={() => {
            useBetslipStore.getState().clearSelections();
            setSubmitPicksAsBet(true);
          }}
          variant="success"
        >
          <PlusIcon /> Add
        </Clamp>
      }
    >
      <div className={styles.flexbox}>
        <div className={styles.copy}>
          Add to your betslip @{" "}
          {constructCurrency(pointsAsOdds, { showCurrencySymbol: false })}
        </div>

        {submitPicksAsBet &&
          uniqueContestMarkets.map((market) => (
            <AddPicksToBetslip
              key={market.event.id}
              eventId={market.event.id}
              entryId={entryId}
              picks={picks}
            />
          ))}
      </div>
    </ClampAlert>
  );
};

export { BetNowCta };
